import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext
} from "react";

import PropTypes from "prop-types";
import gql from "graphql-tag";

import { useApolloClient } from "react-apollo";

import { Field, Control, Radio, Label } from "rbx";

import { ModalContext } from "../../../components/ModalProvider";
import { ReactTable } from "../../../components/DataTable/DataTable";
import { useCurrentUser } from "../../../utils/hooks";
import { generateDriverListColumns } from "./columns";

import EmployeeWeightModal from "../../../components/Employees/EmployeeWeightModal";

export const ALL_DAILY_BOOKINGS_QUERY = gql`
  query DailyBookings($where: DailyBookingWhereInput) {
    DailyBookings(where: $where) {
      RecId
      EmployeeRecIdRef
      Employee {
        RecId
        EmployeeId
        SeniorityNo
        FullName
        HomePhoneNo
        CellPhoneNo
        ShopSteward
        ContactPreference {
          RecId
          CodeId
        }
      }
      BookingRecIdRef
      BookingCodeRecIdRef
      NextCertExp
      Booking {
        RecId
        OperatorRecIdRef
        BookedTrip
        PickupDate
        BookingDate
        Comment
        CreatedDateTime
      }
    }
  }
`;

const useDailyBookings = (date, available = true) => {
  const client = useApolloClient();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { DailyBookings = [] }
      } = await client.query({
        query: ALL_DAILY_BOOKINGS_QUERY,
        variables: {
          where: {
            PickupDate: date,
            Available: available
            // BookingCode_startsWith: available ? "A" : "NA"
          }
        },
        fetchPolicy: "network-only"
      });

      setData(DailyBookings);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }, [available, client, date]);

  useEffect(() => {
    fetchData();
  }, [fetchData, date, available]);

  return { data, loading, fetchData };
};

const DriverList = ({ date, selected, handleSelect, ExcludeOrderStatus }) => {
  const { setModalOpen } = useContext(ModalContext);
  const [bkAvailable, setBkAvailable] = useState(true);
  const { data: user } = useCurrentUser();
  const { data, fetchData } = useDailyBookings(date, bkAvailable);
  const getRowStyles = row => {
    const style = {};
    if (row.original.Employee.ShopSteward) {
      style.background = "rgba(255, 215, 0, 0.2)";
    }
    return style;
  };

  const DRIVER_LIST_COLUMNS = useMemo(() => {
    const handleUpdateDriverWeight = driver => {
      setModalOpen(
        true,
        <EmployeeWeightModal
          driver={driver}
          onComplete={() => setModalOpen(false)}
        />
      );
    };

    return generateDriverListColumns({
      pickupDate: date,
      currentUser: user,
      fetchData,
      selected,
      onSelect: handleSelect,
      BookingCodeWhere: ExcludeOrderStatus
        ? { CodeCatgRecIdRef_not: ExcludeOrderStatus.RecId }
        : {},
      handleUpdateDriverWeight
    });
  }, [
    date,
    user,
    fetchData,
    selected,
    handleSelect,
    ExcludeOrderStatus,
    setModalOpen
  ]);

  return (
    <div>
      <div className="driver-bookings-header">
        <Field>
          <Control>
            <Label>
              <Radio
                defaultChecked
                name="available"
                onChange={() => setBkAvailable(true)}
              />
              &nbsp;Available
            </Label>
            <Label>
              <Radio name="available" onChange={() => setBkAvailable(false)} />
              &nbsp;Not Available
            </Label>
          </Control>
        </Field>
        <Field>
          <Control>
            Showing {bkAvailable ? "Available" : "Not Available"} Drivers
          </Control>
        </Field>
      </div>
      <ReactTable
        columns={DRIVER_LIST_COLUMNS}
        data={data}
        getRowProps={row => ({ style: getRowStyles(row) })}
        sortBy={[{ id: "SeniorityNo", desc: false }]}
      />
    </div>
  );
};

DriverList.propTypes = {
  date: PropTypes.string,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleSelect: PropTypes.func,
  ExcludeOrderStatus: PropTypes.object
};
export default DriverList;
